import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Alert, Button, Card, Checkbox, Field, Select, Spinner, Textarea } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { CostEstimateTable } from './CostEstimateTable';
import { type CostEstimate, type FormLicense, formatMoney, today, usePeopleForm } from './data';

/** Solicitud de alta (guía 7.5): es la creación del usuario; la aprueba RR. HH. viendo el costo de las licencias. */
export function NewHirePage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [organizationId, setOrganizationId] = useState<string | undefined>(undefined);
  const form = usePeopleForm(organizationId);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [positionId, setPositionId] = useState('');
  const [managerId, setManagerId] = useState('');
  const [startDate, setStartDate] = useState(today());
  const [licenseIds, setLicenseIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const estimate = useQuery({
    queryKey: ['usuarios', 'costo', licenseIds, startDate],
    queryFn: () => api<CostEstimate>('/api/usuarios/costo-estimado', { json: { licenseTypeIds: licenseIds, startDate } }),
    enabled: licenseIds.length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(startDate),
    placeholderData: (prev) => prev,
  });

  if (!form.data) return form.error ? <Alert>{errorMessage(form.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  const data = form.data;

  function changeOrganization(id: string) {
    // Departamentos y jefes son de cada organización.
    setOrganizationId(id);
    setDepartmentId('');
    setManagerId('');
  }

  const toggle = (id: string) => setLicenseIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await api<{ number: number }>('/api/usuarios/altas', {
        json: {
          organizationId: data.organizationId,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          departmentId: departmentId || null,
          positionId: positionId || null,
          managerId,
          startDate,
          licenseTypeIds: licenseIds,
          notes: notes.trim() || null,
        },
      });
      await qc.invalidateQueries({ queryKey: ['usuarios'] });
      void navigate(`/usuarios/altas/${created.number}`, { state: { created: true } });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-4">
      <div>
        <Link to="/usuarios/altas" className="text-sm text-marca hover:underline">
          ← {t('us.altas_titulo')}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-tinta">{t('us.altas_nueva')}</h1>
        <p className="text-sm text-slate-600">{t('us.alta_ayuda')}</p>
      </div>
      <Card>
        <form onSubmit={submit} className="space-y-4">
          {error && <Alert>{error}</Alert>}
          {data.organizations.length > 1 && (
            <Select id="us-org" label={t('us.campo_linea')} value={data.organizationId} onChange={(e) => changeOrganization(e.target.value)}>
              {data.organizations.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.groupName} › {o.name}
                </option>
              ))}
            </Select>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="us-nombre" label={t('us.campo_nombre')} required maxLength={100} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            <Field id="us-apellido" label={t('us.campo_apellido')} required maxLength={100} value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
          <Field id="us-correo" type="email" label={t('us.campo_correo')} required maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Select id="us-depto" label={t('us.campo_departamento')} value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
              <option value="">{t('us.sin_elegir')}</option>
              {data.departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
            <Select id="us-cargo" label={t('us.campo_cargo')} value={positionId} onChange={(e) => setPositionId(e.target.value)}>
              <option value="">{t('us.sin_elegir')}</option>
              {data.positions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Select id="us-jefe" label={t('us.campo_jefe')} required value={managerId} onChange={(e) => setManagerId(e.target.value)}>
              <option value="">{t('us.elegir_jefe')}</option>
              {data.managers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.fullName}
                </option>
              ))}
            </Select>
            <Field id="us-ingreso" type="date" label={t('us.campo_ingreso')} required value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-slate-700">{t('us.campo_licencias')}</legend>
            {data.licenses.length === 0 ? (
              <p className="text-sm text-slate-500">{t('us.sin_licencias')}</p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {data.licenses.map((l) => (
                  <Checkbox key={l.id} checked={licenseIds.includes(l.id)} onChange={() => toggle(l.id)} label={<LicenseLabel license={l} />} />
                ))}
              </div>
            )}
          </fieldset>
          {licenseIds.length > 0 && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <p className="mb-2 text-sm font-medium text-slate-700">{t('us.costo_estimado')}</p>
              {estimate.data ? <CostEstimateTable estimate={estimate.data} /> : estimate.error ? <Alert>{errorMessage(estimate.error)}</Alert> : <Spinner label={t('comun.cargando')} />}
            </div>
          )}

          <Textarea id="us-notas" label={t('us.campo_notas')} rows={3} maxLength={2_000} value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || !firstName.trim() || !lastName.trim() || !email.trim() || !managerId || !startDate}>
              {busy ? t('us.enviando') : t('us.enviar')}
            </Button>
            <Link to="/usuarios/altas" className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              {t('comun.cancelar')}
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}

function LicenseLabel({ license: l }: { license: FormLicense }) {
  const free = l.seatsPurchased === null ? null : Math.max(0, l.seatsPurchased - l.assigned);
  return (
    <span>
      <span className="font-medium text-slate-800">{l.name}</span>
      <span className="block text-xs text-slate-500">
        {l.shared
          ? t('us.licencia_compartida')
          : l.billing === 'mensual'
            ? t('us.licencia_mensual', { costo: formatMoney(l.baseCost) })
            : t('us.licencia_anual', { costo: formatMoney(l.baseCost) })}
        {free !== null && ` · ${t('us.puestos_libres', { n: free })}`}
      </span>
    </span>
  );
}
